import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { getCvGeneratorRemoteState, saveCvGeneratorRemoteState } from '../../../services/api';
import { safeLocalStorage } from '../../../util/safeStorage';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CvAny = any;

interface CvData {
  persönlichesProfil: string;
  berufserfahrung: CvAny[];
  fähigkeiten: CvAny[];
  ausbildung: CvAny[];
  weiterbildungen: CvAny[];
  sprachkenntnisse: CvAny[];
  hobbys: CvAny[];
  textfelder: Record<string, CvAny>;
  erfahrungen: Record<string, CvAny>;
  unterschriften: Record<string, CvAny>;
  personalData: Record<string, CvAny>;
  [key: string]: CvAny;
}

interface CvSettings {
  template: string;
  font: string;
  fontSize: number;
  accentColor: string;
}

interface VisibleSections {
  [key: string]: boolean;
}

interface AdditionalSections {
  [key: string]: CvAny;
}

interface CvContextValue {
  cvData: CvData;
  updateCvData: (field: string, value: CvAny) => void;
  visibleSections: VisibleSections;
  toggleSection: (sectionId: string, isVisible: boolean) => void;
  sectionOrder: string[];
  updateSectionOrder: (updater: string[] | ((prev: string[]) => string[])) => void;
  additionalSections: AdditionalSections;
  updateAdditionalSections: (updater: AdditionalSections | ((prev: AdditionalSections) => AdditionalSections)) => void;
  cvSettings: CvSettings;
  setCvSettings: (settings: CvSettings) => void;
  calculateProgress: () => number;
  getProgressHint: () => string;
  lastSaved: Date | null;
}

const CvContext = createContext<CvContextValue | null>(null);

const getStorageKey = (userId?: number) => `cv_generator_data${userId ? `_${userId}` : ''}`;
const getSettingsKey = (userId?: number) => `cv_generator_settings${userId ? `_${userId}` : ''}`;

const loadFromStorage = <T,>(key: string, defaultValue: T): T => {
  try {
    const item = safeLocalStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (e) {
    console.warn('Failed to load from localStorage:', e);
    return defaultValue;
  }
};

const saveToStorage = (key: string, data: CvAny): boolean => {
  try {
    safeLocalStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (e) {
    console.warn('Failed to save to localStorage:', e);
    return false;
  }
};

/** Normalize raw CV JSON (localStorage or API) into a safe CvData shape. */
const normalizeCvDataLoaded = (raw: CvAny): CvData => {
  const loadedData = { ...DEFAULT_CV_DATA, ...raw } as CvData;
  if (!Array.isArray(loadedData.hobbys)) loadedData.hobbys = [];
  if (Array.isArray(loadedData.sprachkenntnisse)) {
    loadedData.sprachkenntnisse = loadedData.sprachkenntnisse.map((lang: CvAny) => {
      if (lang.sprachniveau === 'true' || lang.sprachniveau === 'false' || lang.sprachniveau === true || lang.sprachniveau === false) {
        return { ...lang, sprachniveau: '' };
      }
      return lang;
    });
  }
  return loadedData;
};

const mergeVisibleFromRemote = (raw: CvAny): VisibleSections => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_VISIBLE };
  return { ...DEFAULT_VISIBLE, ...raw };
};

const mergeSettingsFromRemote = (raw: CvAny): CvSettings => {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SETTINGS };
  return { ...DEFAULT_SETTINGS, ...raw };
};

/**
 * Check if CV data is essentially empty (no user-entered content).
 */
const isCvDataEmpty = (data: CvData): boolean => {
  const hasPersonalData = data.personalData && Object.values(data.personalData).some(
    (v) => v && v !== ''
  );
  const hasProfile = !!data.persönlichesProfil;
  const hasWork = data.berufserfahrung && data.berufserfahrung.length > 0;
  const hasSkills = data.fähigkeiten && data.fähigkeiten.length > 0;
  const hasEducation = data.ausbildung && data.ausbildung.length > 0;
  const hasLanguages = data.sprachkenntnisse && data.sprachkenntnisse.length > 0;
  return !hasPersonalData && !hasProfile && !hasWork && !hasSkills && !hasEducation && !hasLanguages;
};

/**
 * Build prefilled CV data from candidate profile.
 */
const buildCvDataFromCandidate = (candidate: CvAny, userEmail?: string): Partial<CvData> => {
  const result: Partial<CvData> = {};

  // Personal data
  const personalData: Record<string, CvAny> = {};
  if (candidate.contact?.first_name) personalData.vorname = candidate.contact.first_name;
  if (candidate.contact?.last_name) personalData.nachname = candidate.contact.last_name;
  if (candidate.contact?.email) personalData.email = candidate.contact.email;
  else if (userEmail) personalData.email = userEmail;
  if (candidate.contact?.phone) personalData.telefonnummer = candidate.contact.phone;
  if (candidate.location?.street) personalData.adresse = candidate.location.street;
  if (candidate.location?.postal_code) personalData.postleitzahl = candidate.location.postal_code;
  if (candidate.location?.city) personalData.stadt = candidate.location.city;
  if (candidate.display?.avatar_link) personalData.foto = candidate.display.avatar_link;

  if (Object.keys(personalData).length > 0) {
    result.personalData = personalData;
  }

  // Personal profile from self_description
  if (candidate.additional?.self_description) {
    result.persönlichesProfil = candidate.additional.self_description;
  }

  // Work experience from last_employer and previous_employer
  const workExperience: CvAny[] = [];
  if (candidate.display?.last_employer?.employer_name) {
    const emp = candidate.display.last_employer;
    workExperience.push({
      position: emp.employer_department || candidate.main_job?.job_position || '',
      unternehmen: emp.employer_name || '',
      standort: '',
      startdatum: '',
      enddatum: '',
      aktuelleArbeitsstelle: true,
      zusammenfassung: '',
    });
  }
  if (candidate.display?.previous_employer?.employer_name) {
    const emp = candidate.display.previous_employer;
    workExperience.push({
      position: emp.employer_department || '',
      unternehmen: emp.employer_name || '',
      standort: '',
      startdatum: '',
      enddatum: '',
      aktuelleArbeitsstelle: false,
      zusammenfassung: '',
    });
  }
  if (workExperience.length > 0) {
    result.berufserfahrung = workExperience;
  }

  // Education
  if (candidate.education?.degree || candidate.education?.level) {
    result.ausbildung = [{
      ausbildungsstaette: '',
      abschluss: candidate.education.degree || candidate.education.level || '',
      standort: '',
      startdatum: '',
      enddatum: '',
      ichStudiereNoch: false,
      zusammenfassung: '',
    }];
  }

  // Skills from specialization, software, IT
  const skills: CvAny[] = [];
  if (candidate.main_job?.specialization) {
    skills.push({ name: candidate.main_job.specialization, niveau: '' });
  }
  if (candidate.education?.software) {
    candidate.education.software.split(',').map((s: string) => s.trim()).filter(Boolean).forEach((s: string) => {
      skills.push({ name: s, niveau: '' });
    });
  }
  if (candidate.additional?.it) {
    candidate.additional.it.split(',').map((s: string) => s.trim()).filter(Boolean).forEach((s: string) => {
      if (!skills.some((sk) => sk.name.toLowerCase() === s.toLowerCase())) {
        skills.push({ name: s, niveau: '' });
      }
    });
  }
  if (skills.length > 0) {
    result.fähigkeiten = skills;
  }

  // Language skills — values are "true"/"false" strings indicating if the candidate speaks the language
  const languages: CvAny[] = [];
  if (candidate.additional?.language_german === 'true' || candidate.additional?.language_german === true) {
    languages.push({ sprache: 'Deutsch', sprachniveau: '' });
  }
  if (candidate.additional?.language_english === 'true' || candidate.additional?.language_english === true) {
    languages.push({ sprache: 'Englisch', sprachniveau: '' });
  }
  if (languages.length > 0) {
    result.sprachkenntnisse = languages;
  }

  return result;
};

const DEFAULT_CV_DATA: CvData = {
  persönlichesProfil: '',
  berufserfahrung: [],
  fähigkeiten: [],
  ausbildung: [],
  weiterbildungen: [],
  sprachkenntnisse: [],
  hobbys: [],
  textfelder: {},
  erfahrungen: {},
  unterschriften: {},
  personalData: {},
};

const DEFAULT_VISIBLE: VisibleSections = {
  personalProfile: true,
  workExperience: true,
  skills: true,
  education: true,
  weiterbildungen: false,
  sprachkenntnisse: false,
};

const DEFAULT_ORDER = [
  'personalData',
  'personalProfile',
  'workExperience',
  'skills',
  'education',
  'weiterbildungen',
  'sprachkenntnisse',
  'sections',
];

const DEFAULT_SETTINGS: CvSettings = {
  template: 'hopkins',
  font: 'DM Sans',
  fontSize: 100,
  accentColor: 'schwarz',
};

export const CvProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const userId = user?.id;
  const prevUserIdRef = useRef<number | undefined>(undefined);
  const allowRemoteSaveRef = useRef(true);
  const remoteFetchGenRef = useRef(0);

  const [cvData, setCvData] = useState<CvData>(() => {
    const loaded = loadFromStorage<CvData>(getStorageKey(), { ...DEFAULT_CV_DATA });
    return normalizeCvDataLoaded(loaded);
  });

  const [visibleSections, setVisibleSections] = useState<VisibleSections>(() =>
    loadFromStorage(`${getSettingsKey()}_visible`, { ...DEFAULT_VISIBLE })
  );

  const [sectionOrder, setSectionOrder] = useState<string[]>(() =>
    loadFromStorage(`${getSettingsKey()}_order`, [...DEFAULT_ORDER])
  );

  const [additionalSections, setAdditionalSections] = useState<AdditionalSections>(() =>
    loadFromStorage(`${getSettingsKey()}_additional`, {})
  );

  const [cvSettings, setCvSettings] = useState<CvSettings>(() =>
    loadFromStorage(`${getSettingsKey()}_cv`, { ...DEFAULT_SETTINGS })
  );

  // When user changes (login/switch), reload all state from per-user storage
  useEffect(() => {
    if (userId === prevUserIdRef.current) return;
    prevUserIdRef.current = userId;

    const storageKey = getStorageKey(userId);
    const settingsKey = getSettingsKey(userId);

    const loadedData = normalizeCvDataLoaded(
      loadFromStorage<CvData>(storageKey, { ...DEFAULT_CV_DATA })
    );

    // Check if this user has empty CV data and we can prefill from candidate profile
    const candidate = user?._candidate;
    if (isCvDataEmpty(loadedData) && candidate) {
      const prefilled = buildCvDataFromCandidate(candidate, user?.email);
      if (Object.keys(prefilled).length > 0) {
        Object.assign(loadedData, prefilled);
      }
    }

    setCvData(loadedData);
    const loadedVisible = loadFromStorage(`${settingsKey}_visible`, { ...DEFAULT_VISIBLE });
    // Auto-enable language section if we have prefilled language data
    if (loadedData.sprachkenntnisse && loadedData.sprachkenntnisse.length > 0) {
      loadedVisible.sprachkenntnisse = true;
    }
    setVisibleSections(loadedVisible);
    setSectionOrder(loadFromStorage(`${settingsKey}_order`, [...DEFAULT_ORDER]));
    setAdditionalSections(loadFromStorage(`${settingsKey}_additional`, {}));
    setCvSettings(loadFromStorage(`${settingsKey}_cv`, { ...DEFAULT_SETTINGS }));
  }, [userId, user?._candidate, user?.email]);

  // Load from API after local cache; DB wins when a row exists. Block remote POST until this finishes to avoid clobbering server data with stale local state on login.
  useEffect(() => {
    if (!isAuthenticated) {
      allowRemoteSaveRef.current = true;
      return;
    }
    if (!userId) {
      allowRemoteSaveRef.current = false;
      return;
    }

    allowRemoteSaveRef.current = false;
    const gen = ++remoteFetchGenRef.current;
    let cancelled = false;

    void (async () => {
      try {
        const payload = await getCvGeneratorRemoteState();
        if (cancelled || gen !== remoteFetchGenRef.current) return;
        if (payload.has_record) {
          const storageKey = getStorageKey(userId);
          const settingsKey = getSettingsKey(userId);

          if (payload.cv_data != null && typeof payload.cv_data === 'object') {
            const nextCv = normalizeCvDataLoaded(payload.cv_data);
            setCvData(nextCv);
            saveToStorage(storageKey, nextCv);
          }
          if (payload.visible_sections != null && typeof payload.visible_sections === 'object') {
            const merged = mergeVisibleFromRemote(payload.visible_sections);
            setVisibleSections(merged);
            saveToStorage(`${settingsKey}_visible`, merged);
          }
          if (Array.isArray(payload.section_order)) {
            setSectionOrder(payload.section_order);
            saveToStorage(`${settingsKey}_order`, payload.section_order);
          }
          if (payload.additional_sections != null && typeof payload.additional_sections === 'object') {
            const nextAdd = payload.additional_sections as AdditionalSections;
            setAdditionalSections(nextAdd);
            saveToStorage(`${settingsKey}_additional`, nextAdd);
          }
          if (payload.cv_settings != null && typeof payload.cv_settings === 'object') {
            const nextSettings = mergeSettingsFromRemote(payload.cv_settings);
            setCvSettings(nextSettings);
            saveToStorage(`${settingsKey}_cv`, nextSettings);
          }
        }
      } catch (e) {
        console.warn('Failed to load CV data from server:', e);
      } finally {
        if (!cancelled && gen === remoteFetchGenRef.current) {
          allowRemoteSaveRef.current = true;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId]);

  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const autoSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const storageKey = getStorageKey(userId);
      const settingsKey = getSettingsKey(userId);
      const saved = saveToStorage(storageKey, cvData);
      if (saved) {
        saveToStorage(`${settingsKey}_visible`, visibleSections);
        saveToStorage(`${settingsKey}_order`, sectionOrder);
        saveToStorage(`${settingsKey}_additional`, additionalSections);
        saveToStorage(`${settingsKey}_cv`, cvSettings);
        setLastSaved(new Date());
      }

      const canPostRemote =
        saved &&
        isAuthenticated &&
        userId != null &&
        allowRemoteSaveRef.current;

      if (canPostRemote) {
        void saveCvGeneratorRemoteState({
          cv_data: cvData as unknown as Record<string, unknown>,
          visible_sections: visibleSections,
          section_order: sectionOrder,
          additional_sections: additionalSections as Record<string, unknown>,
          cv_settings: cvSettings as unknown as Record<string, unknown>,
        }).catch((err) => {
          console.warn('Failed to save CV data to server:', err);
        });
      }
    }, 1000);
  };

  useEffect(() => {
    autoSave();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvData, visibleSections, sectionOrder, additionalSections, cvSettings, userId, isAuthenticated]);

  const updateCvData = (field: string, value: CvAny) => {
    setCvData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleSection = (sectionId: string, isVisible: boolean) => {
    setVisibleSections((prev) => ({
      ...prev,
      [sectionId]: isVisible,
    }));
  };

  const updateSectionOrder = (updater: string[] | ((prev: string[]) => string[])) => {
    setSectionOrder((prev) =>
      typeof updater === 'function' ? updater(prev) : updater
    );
  };

  const updateAdditionalSections = (updater: AdditionalSections | ((prev: AdditionalSections) => AdditionalSections)) => {
    setAdditionalSections((prev) =>
      typeof updater === 'function' ? updater(prev) : updater
    );
  };

  const calculateProgress = (): number => {
    let completed = 0;
    const total = 4;

    if (cvData.persönlichesProfil) completed += 1;
    if (cvData.berufserfahrung && cvData.berufserfahrung.length > 0) completed += 1;
    if (cvData.fähigkeiten && cvData.fähigkeiten.length > 0) completed += 1;
    if (cvData.ausbildung && cvData.ausbildung.length > 0) completed += 1;

    return Math.round((completed / total) * 100);
  };

  const getProgressHint = (): string => {
    const progress = calculateProgress();
    if (progress < 25) return 'Beginnen Sie mit Ihrem Profil und Ihrer aktuellen Position.';
    if (progress < 50) return 'Fügen Sie Berufserfahrung hinzu, um Ihren Werdegang zu zeigen.';
    if (progress < 75) return 'Ergänzen Sie Fähigkeiten und Ausbildung, um Ihr Profil zu schärfen.';
    if (progress < 100) return 'Feinschliff: Details prüfen und ggf. weitere Abschnitte ergänzen.';
    return 'Ihr Lebenslauf ist weitgehend vollständig – jetzt können Sie ihn exportieren.';
  };

  const value: CvContextValue = {
    cvData,
    updateCvData,
    visibleSections,
    toggleSection,
    sectionOrder,
    updateSectionOrder,
    additionalSections,
    updateAdditionalSections,
    cvSettings,
    setCvSettings,
    calculateProgress,
    getProgressHint,
    lastSaved,
  };

  return <CvContext.Provider value={value}>{children}</CvContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useCv = (): CvContextValue => {
  const ctx = useContext(CvContext);
  if (!ctx) {
    throw new Error('useCv must be used within a CvProvider');
  }
  return ctx;
};
